import React, {
  Fragment,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { v4 } from "uuid";

import { Drawer, SearchEmpty, ModalDefault } from "components";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";
import { usePlaceGate } from "hooks/usePlaceGate";

import { useAppSelector } from "store/rootReducer";

import { IMarkingType } from "features/fullview/RightTemplate/Mark/interface";
import { LoadSpinner } from "features/LoadSpinner";
import { Page } from "features/RightAudienceComponent/types";

import api from "services/api";
import { customerTemplateService } from "services/costumerTemplate";
import { HearingService } from "services/hearing";
import {
  hearingTranscriptions,
  hearingTranscriptionsV2,
} from "services/hearingTranscriptions";
import { markingService } from "services/marking";
import { IHearingIndexUpdate } from "services/marking/interface";
import { HearingMinuteServices } from "services/minute";
import { transcriptionServices } from "services/transcription";

import { isExternalSourcePlatform } from "utils/entities/hearingFilePlatform";
import { LogOperation } from "utils/entities/logOperation";
import { FORMS_HEARING } from "utils/entities/permission";
import { formatYmd } from "utils/format";
import { useQuery } from "utils/get";

import { MarkingChildrenModal } from "./MarkingChildren";
import { Container, FullViewComponente } from "./styles";

let countdownTimeout: NodeJS.Timeout;
let countdownTimeoutIndex: NodeJS.Timeout;

export const FullViewPage: React.FC = () => {
  const { t } = useTranslation();
  const query = useQuery();
  const location = useLocation();
  const navigate = useNavigate();

  const queryString = window.location.search;
  const defaultPage = location.state?.page;
  const audience = location.state?.audience;

  const { addToast } = useToast();
  const [transcriptionList, setTranscriptionList] = useState<Array<any>>([]);
  const [requestStatus, setRequestStatus] = useState<boolean>(false);

  const summaryRef: MutableRefObject<any> = useRef(null);
  const AtaRef: MutableRefObject<any> = useRef(null);
  const transcriptionRef: MutableRefObject<any> = useRef(null);

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [markingList, setMarkingList] = useState<Array<any>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isBegining, setIsBegining] = useState(true);
  const [attendantId, setAttendantId] = useState<number | undefined>();
  const [markingError, setMarkingError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemMarking, setItemMarking] = useState<any | null>(null);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState("");
  const [modelList, setModelList] = useState<any>([]);
  const [transcriptionSelect, setTranscriptionSelect] = useState<string>("");
  const [page, setPage] = useState<null | number>(null);
  const [selectTemplate, setSelectTemplate] = useState<any>();
  const [ata, setAta] = useState<any>([]);
  const [ataText, setAtaText] = useState("");
  const [selectAtaTemplate, setSelectAtaTemplate] = useState<any>();
  const [loadingAba, setLoadingAba] = useState(false);
  const { user } = useAppSelector((state) => state.user);
  const { userCustomerProfile } = useAppSelector((state) => state.profile);
  const [showModelInput, setShowModelInput] = useState<boolean>(false);
  const [marking, setMarking] = useState<IMarkingType>({
    toggleMarkingTime: () => ({}),
    toggleRate: (_rate: number) => ({}),
    toggleVideoPlay: () => ({}),
    hearingRecord: {},
    playerState: {
      playing: false,
      progress: 0,
      currentTime: "00:00",
      duration: "00:00",
      offset1: "00:00",
      offset2: "00:00",
      secondsCurrentTime: 0,
      secondsDuration: 0,
      volume: 1,
      playbackRate: 1,
      marking: null,
    },
  });

  const [changedTextAta, setChangedTextAta] = useState<boolean>(true);

  const [requestStatusIndex, setRequestStatusIndex] = useState<boolean>(false);
  const [loadingTranscription, setLoadingTranscription] =
    useState<boolean>(false);
  const [submited, setSubmited] = useState<boolean>(false);

  const { read } = usePermissions(FORMS_HEARING.HEARING_FILE);
  const hearingFileId = query.get("hearingFileId");
  const hearingId = query.get("hearingId");
  const placeId = query.get("placeId");
  const guard = usePlaceGate({ placeId: placeId });

  const authorized = guard.isAllowed;
  const checkingAuthorization = !guard.isReady;

  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);

  function timeToSeconds(timeString: string) {
    const timeArray = timeString.split(":");
    const minutes = parseInt(timeArray[0]);
    const seconds = parseInt(timeArray[1]);
    const totalSeconds = minutes * 60 + seconds;
    return totalSeconds;
  }

  function secondsToTime(seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = ("0" + minutes).slice(-2);
    const formattedSeconds = ("0" + remainingSeconds).slice(-2);
    const timeString = formattedMinutes + ":" + formattedSeconds;
    return timeString;
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    closeModal();
  };

  const onDeleteMarking = (item: any) => {
    setItemMarking(item);
    setShowDeleteModal(true);
  };

  const deleteMarking = async () => {
    try {
      closeDeleteModal();
      setLoading(true);
      await markingService.delete(itemMarking?.id);
      await LogOperation.postLogOperation("HearingIndex", 3, itemMarking?.id);
      await getIndexList();
      setLoading(false);
      addToast({
        title: t("markings.delete"),
        description: t("markings.deleted"),
        type: "success",
      });
    } catch {
      setLoading(false);
      setCurrentIndex(-1);
      addToast({
        title: t("markings.delete"),
        description: t("markings.deleteError"),
        type: "warning",
      });
    }
  };

  const getIndexList = async () => {
    try {
      const { data } = await api.get(
        `/v1/HearingIndex/GetAllByHearingFileId?hearingFileId=${hearingFileId}`,
      );
      if (data?.data?.length > 0) {
        const responseSorted = data.data.sort((a: any, b: any) => {
          return a.offSet > b.offSet ? 0 : -1;
        });
        setMarkingList(responseSorted);
        if (data.data.find((x: any) => x.status >= 0 && x.status < 3)) {
          return setRequestStatusIndex(true);
        }
        setRequestStatusIndex(false);
      } else {
        setMarkingList(data.data);
      }
    } catch {
      setLoading(false);
      addToast({
        title: t("audiences.messages.loadError"),
        description: t("audiences.messages.loadErrorDescription"),
        type: "warning",
      });
    }
  };

  const onEditMarking = (item: any) => {
    setItemMarking(item);
    setMarking({
      ...marking,
      playerState: {
        ...marking.playerState,
        offset1: secondsToTime(item.offSet1),
        offset2: secondsToTime(item.offSet2),
      },
    });
    setAttendantId(item.attendantId);
    setIsOpen(true);
    setIsEdit(true);
    setSubject(item.subject);
  };

  async function processarCortes() {
    if (markingList?.length > 0) {
      try {
        await api.post(
          "/v1/HearingIndex/SplitUpVideoByHearingFileId",
          {
            device: LogOperation.getDevice(),
            ip: LogOperation.getMachineIP(),
            logPoliceUnitId: LogOperation.getPoliceUnitId(),
            logUserId: LogOperation.getUserId(),
          },
          {
            params: {
              hearingFileId: Number(hearingFileId),
            },
          },
        );
        addToast({
          type: "success",
          title: t("common.success"),
          description: t("markings.processCuts"),
        });
        await LogOperation.postLogOperation("HearingIndex", 8, 8);

        await getIndexList();
      } catch (error) {
        if (error instanceof AxiosError) {
          addToast({
            type: "error",
            title: t("common.error"),
            description: error.response?.data?.message,
          });
        } else {
          addToast({
            title: t("markings.processCutsError"),
            description: t("markings.processCutsErrorDescription"),
            type: "warning",
          });
        }
      }
    }
  }

  useEffect(() => {
    if (!checkingAuthorization && authorized) {
      (async () => {
        try {
          setLoading(true);
          await getAllByHearingFileId();
          await onUpdateStatusIfNotFinished();
          await getIndexList();
          setLoading(false);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [checkingAuthorization, authorized]);

  const onUpdateStatusIfNotFinished = async () => {
    while (requestStatus) {
      const timeout = setTimeout(() => {
        (async () => {
          await getAllByHearingFileId();
        })();
      }, 5000);

      clearTimeout(timeout);
    }
  };

  function resetCountdown() {
    clearTimeout(countdownTimeout);
    setRequestStatus(false);
  }

  function resetCountdownIndex() {
    clearTimeout(countdownTimeoutIndex);
    setRequestStatusIndex(false);
  }

  useEffect(() => {
    if (requestStatus) {
      countdownTimeout = setTimeout(() => {
        (async () => {
          await getAllByHearingFileId();
        })();
      }, 5000);
    } else {
      resetCountdown();
    }
  }, [transcriptionList, requestStatus]);

  useEffect(() => {
    if (requestStatusIndex) {
      countdownTimeoutIndex = setTimeout(() => {
        (async () => {
          await getIndexList();
        })();
      }, 10000);
    } else {
      resetCountdownIndex();
    }
  }, [markingList, requestStatusIndex]);

  async function createTranscription() {
    setLoadingTranscription(true);
    const hearingFileId = query.get("hearingFileId");

    if (transcriptionSelect === "automatico") {
      if (!selectTemplate) {
        setLoadingTranscription(false);
        return addToast({
          type: "warning",
          title: t("common.warning"),
          description: t("transcription.selectModel"),
        });
      }

      const data = await transcriptionServices.addTranscriptionUI({
        active: true,
        autoTranscription: true,
        device: LogOperation.getDevice(),
        sendEmail: true,
        guid: v4(),
        title: `Transcrição da audiencia nº: ${hearingFileId}`,
        hearingFileId: Number(hearingFileId),
        customerTemplateId: selectTemplate.id,
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: LogOperation.getPoliceUnitId(),
        logUserId: LogOperation.getUserId(),
        userId: user?.id,
      });

      if (data.ok) {
        setShowModelInput(false);
        getAllByHearingFileId();

        addToast({
          type: "success",
          title: t("transcription.automatic"),
          description: t("transcription.started"),
        });
      } else {
        addToast({
          type: "warning",
          title: t("transcription.automatic"),
          description: data.data.message,
        });
      }
    }

    if (transcriptionSelect === "manual") {
      const data = {
        id: 0,
        guid: v4(),
        hearingFileId: Number(hearingFileId),
        userId: user?.id,
        customerTemplateId: modelList[0]?.id,
        title: `Transcrição da audiencia nº: ${hearingFileId}`,
        autoTranscription: false,
        createIn: formatYmd(new Date()),
        updateIn: formatYmd(new Date()),
        status: 6,
        sendEmail: false,
        active: true,
        device: LogOperation.getDevice(),
        ip: LogOperation.getMachineIP(),
        logPoliceUnitId: LogOperation.getPoliceUnitId(),
        logUserId: LogOperation.getUserId(),
      };
      const { success } = await hearingTranscriptionsV2.addTranscription(data);
      setSelectTemplate(modelList[0]);
      setShowModelInput(true);
      if (success) getAllByHearingFileId();
      addToast({
        type: "success",
        title: t("transcription.manual"),
        description: t("transcription.created"),
      });
    }
    setLoadingTranscription(false);
  }

  async function updateTranscription(by: "summary" | "transcription") {
    try {
      if (
        transcriptionList &&
        transcriptionList[0] &&
        transcriptionList[0].autoTranscription
      ) {
        await transcriptionServices.patchDisableAutoTranscription(
          transcriptionList[0]?.id,
        );
      }

      const ref = by === "transcription" ? transcriptionRef : summaryRef;
      if (!ref.current || !ref.current.getContent()) return;

      const patchPayload =
        by === "transcription"
          ? { textPlain: ref.current.getContent(), id: transcriptionList[0]?.id }
          : { textPlainSummary: ref.current.getContent(), id: transcriptionList[0]?.id };

      const { success } = await transcriptionServices.patch(patchPayload);

      await getAllByHearingFileId();

      if (by === "transcription") {
        await transcriptionServices.patchTemplate({
          id: transcriptionList[0]?.id,
          customerTemplateId: selectTemplate?.id || modelList[0]?.id,
        });
      }

      const label = by === "transcription" ? "transcription" : "summary";
      if (success) {
        addToast({
          type: "success",
          title: t(`${label}.updated`),
          description: t(`${label}.updatedSuccess`),
        });
      } else {
        addToast({
          type: "warning",
          title: t(`${label}.notUpdated`),
          description: t(`${label}.notUpdatedDescription`),
        });
      }
    } catch {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("common.unexpectedError"),
      });
    }
  }

  async function getAllByHearingFileId() {
    const hearingFileId = query.get("hearingFileId");

    const { data, success } =
      await hearingTranscriptionsV2.getAllByHearingFileId(
        Number(hearingFileId),
      );

    if (success) {
      setTranscriptionList(data);
      if (data && data[0] && data[0]?.status !== 6) {
        return setRequestStatus(true);
      }

      setRequestStatus(false);
    }
  }

  async function tryAgain() {
    const hearingTranscriptionServices = hearingTranscriptions;

    if (
      transcriptionList.length &&
      transcriptionList[0] &&
      transcriptionList[0].id
    ) {
      await hearingTranscriptionServices.deleteHearingTranscriptionById(
        Number(transcriptionList[0].id),
      );

      await createTranscription();
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setMarkingError(false);
      setHasTriedSubmit(false);
    }
  }, [isOpen]);

  const closeModal = () => {
    setIsOpen(false);
    setIsEdit(false);
    setItemMarking(null);
    setCurrentIndex(-1);
    setMarkingError(false);
    setHasTriedSubmit(false);
  };

  const finishCorrection = () => {
    setSubject("");
    setMarking({
      toggleMarkingTime: () => ({}),
      toggleRate: (_rate: number) => ({}),
      toggleVideoPlay: () => ({}),
      hearingRecord: {},
      playerState: {
        playing: false,
        progress: 0,
        currentTime: marking.playerState.offset2,
        duration: "00:00",
        offset1: marking.playerState.offset2,
        offset2: "00:00",
        secondsCurrentTime: 0,
        secondsDuration: 0,
        volume: 1,
        playbackRate: 1,
        marking: null,
      },
    });
  };

  const createNewMarking = async () => {
    setIsBegining(false);

    try {
      setHasTriedSubmit(true);

      if (!attendantId) {
        addToast({
          title: t("markings.creationTitle"),
          description: t("markings.selectParticipant"),
          type: "warning",
        });

        setMarkingError(true);
        return;
      }

      if (
        !isBegining &&
        marking.playerState.offset2 < marking.playerState.offset1
      ) {
        addToast({
          title: t("markings.invalid"),
          description: t("markings.invalidEndBeforeStart"),
          type: "warning",
        });

        closeModal();
        return;
      }

      if (
        !isBegining &&
        marking.playerState.offset1 === "00:00" &&
        marking.playerState.offset2 === "00:00"
      ) {
        addToast({
          title: t("markings.invalid"),
          description: t("markings.invalidZeroTime"),
          type: "warning",
        });

        closeModal();
        return;
      }

      if (!isBegining && !subject?.trim()) {
        setMarkingError(true);
        addToast({
          title: t("markings.invalid"),
          description: t("markings.fillFields"),
          type: "warning",
        });
        return;
      }

      if (!isBegining) {
        const body = {
          hearingId: Number(hearingId),
          hearingFileId: Number(hearingFileId),
          attendantId: attendantId,
          offSet1: Number(timeToSeconds(marking.playerState.offset1)),
          offSet2: Number(timeToSeconds(marking.playerState.offset2)),
          recordDate: new Date(),
          duration: marking.playerState.secondsDuration,
          subject: subject,
          device: LogOperation.getDevice(),
          ip: LogOperation.getMachineIP(),
          logPoliceUnitId: LogOperation.getPoliceUnitId(),
          logUserId: LogOperation.getUserId(),
        };

        await api.post("/v1/HearingIndex/Add", body);

        await getIndexList();

        closeModal();
        finishCorrection();
        setIsBegining(true);
        addToast({
          title: t("markings.finished"),
          description: t("markings.created"),
          type: "success",
        });
      } else {
        addToast({
          title: t("markings.started"),
          description: t("markings.startedStep"),
          type: "success",
        });
      }
      closeModal();
    } catch (error) {
      if (error instanceof AxiosError) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: error.response?.data.message,
        });
      } else {
        addToast({
          title: t("markings.errorTitle"),
          description: t("markings.errorDescription"),
          type: "warning",
        });
      }
    }
  };

  function getRawQueryValue(param: any) {
    const rawValue = queryString.split(`${param}=`)[1];
    return rawValue ? rawValue.split("&").join("&") : null;
  }

  const handleUpdateMarking = async () => {
    try {
      setHasTriedSubmit(true);

      if (!subject?.trim()) {
        setMarkingError(true);
        addToast({
          title: t("markings.invalid"),
          description: t("markings.fillFields"),
          type: "warning",
        });
        return;
      }

      if (itemMarking && attendantId) {
        const body: IHearingIndexUpdate = {
          id: itemMarking?.id,
          hearingId: itemMarking.hearingId,
          attendantId,
          hearingFileId: itemMarking.hearingFileId,
          offset1: timeToSeconds(marking.playerState.offset1),
          offset2: timeToSeconds(marking.playerState.offset2),
          duration: marking.playerState.secondsDuration,
          subject: subject,
          status: itemMarking.status,
          device: LogOperation.getDevice(),
          ip: LogOperation.getMachineIP(),
          logPoliceUnitId: LogOperation.getPoliceUnitId(),
          logUserId: LogOperation.getUserId(),
        };

        await markingService.update(body);
        await getIndexList();
        closeModal();

        addToast({
          title: t("markings.updateTitle"),
          description: t("markings.updated"),
          type: "success",
        });
      }
    } catch {
      addToast({
        title: t("markings.updateTitle"),
        description: t("markings.updateError"),
        type: "warning",
      });
    }
  };

  function summaryFunction() {
    if (!(transcriptionList && transcriptionList[0])) {
      addToast({
        type: "warning",
        title: t("transcription.startTranscription"),
        description: t("transcription.startToSave"),
      });
    }
  }

  function shouldDisableButton(): boolean {
    if (!transcriptionList || transcriptionList.length === 0) return true;
    return transcriptionList[0].status !== 6;
  }

  function getButtonTitle(): string {
    if (!transcriptionList || transcriptionList.length === 0)
      return t("common.start");
    return transcriptionList[0].status !== 6
      ? t("common.start")
      : t("common.save");
  }

  const summary = {
    buttonFunction:
      transcriptionList &&
      transcriptionList[0] &&
      transcriptionList[0]?.status === 6
        ? () => updateTranscription("summary")
        : () => summaryFunction(),
    buttonTitle: getButtonTitle(),
    disabledButton: shouldDisableButton(),
    status:
      transcriptionList && transcriptionList[0]
        ? transcriptionList[0]?.status
        : null,
  };

  const transcription = {
    loadingTranscription,
    buttonFunction:
      transcriptionList &&
      transcriptionList[0] &&
      transcriptionList[0]?.status === 6
        ? () => updateTranscription("transcription")
        : createTranscription,
    buttonTitle: getButtonTitle(),
    disabledButton: shouldDisableButton(),
    status:
      transcriptionList && transcriptionList[0]
        ? transcriptionList[0]?.status
        : null,
  };

  const loadingModel = async (type: number) => {
    setModelList([]);
    try {
      const { body, success } =
        await customerTemplateService.getAllByCustomerId({
          active: true,
          customerId: LogOperation.getCustomerId(),
          type: type,
        });

      if (success) {
        const list = body.map((item: any) => ({ ...item, name: item.title }));
        setModelList(list);
      }
      setLoadingAba(false);
    } catch (error) {
      return error;
    }
    setLoadingAba(false);
  };

  useEffect(() => {
    const currentPage = page === null ? defaultPage : page;
    if (currentPage === Page.TRANSCRIPTION || currentPage === Page.ATA) {
      loadingModel(currentPage === Page.ATA ? 1 : 2);
    }
    setSubmited(false);
  }, [page, defaultPage]);

  const getAtaList = async () => {
    const { success, body } = await HearingMinuteServices.getAllByHearingId({
      hearingId: hearingId ? hearingId : "",
    });

    if (success) {
      setAta(body);
      setAtaText(body?.[0]?.content || "");
    }
    if (!success) {
      addToast({
        type: "warning",
        title: t("audiences.minutes"),
        description: body,
      });
    }
  };

  useEffect(() => {
    if (defaultPage === Page.ATA || page === Page.ATA) {
      getAtaList();
    }
  }, [defaultPage, page]);

  const saveAta = async () => {
    if (!selectAtaTemplate?.id) {
      return addToast({
        type: "warning",
        title: t("audiences.minutes"),
        description: t("audiences.messages.selectMinutesTemplate"),
      });
    }

    if (!AtaRef.current || !AtaRef.current.getContent()) {
      return addToast({
        type: "warning",
        title: t("audiences.minutes"),
        description: t("audiences.messages.minutesEmpty"),
      });
    }

    try {
      const isUpdate = ata && ata[0] && ata[0]?.id;
      const { success } = isUpdate
        ? await HearingMinuteServices.update({
            id: ata[0]?.id,
            hearingId: hearingId || "",
            content: AtaRef.current.getContent(),
            customerTemplateId: selectAtaTemplate?.id,
          })
        : await HearingMinuteServices.add({
            hearingId: hearingId || "",
            content: AtaRef.current.getContent(),
            customerTemplateId: selectAtaTemplate?.id ?? 0,
          });

      if (isUpdate && ata[0]?.id) {
        await HearingMinuteServices.patchTemplate({
          id: ata[0]?.id,
          customerTemplateId: selectAtaTemplate?.id,
        });
      }

      await getAtaList();

      if (success) {
        addToast({
          type: "success",
          title: t("audiences.minutes"),
          description: t(
            isUpdate
              ? "audiences.messages.minutesUpdated"
              : "audiences.messages.minutesSaved",
          ),
        });
      } else {
        addToast({
          type: "warning",
          title: t("audiences.minutes"),
          description: t(
            isUpdate
              ? "audiences.messages.minutesNotUpdated"
              : "audiences.messages.minutesNotSaved",
          ),
        });
      }
    } catch {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("common.unexpectedError"),
      });
    }
  };

  if (!guard.isReady) {
    return (
      <Fragment>
        <Container>
          <LoadSpinner loading />
        </Container>
      </Fragment>
    );
  }

  if (!authorized) {
    navigate("/dashboard", { replace: true });
  }

  return (
    <Fragment>
      <Container>
        {read ? (
          <FullViewComponente
            loadingAba={loadingAba}
            key={hearingId}
            setPage={setPage}
            summary={{
              ...summary,
              ataRef: summaryRef,
              transcription: transcriptionList,
            }}
            mark={{
              currentIndex,
              setCurrentIndex,
              data: markingList,
              marking: marking,
              setHearingItemDto: () => {},
              onDeleteMarking,
              onEditMarking,
              isBeginin: isBegining,
              processCuts: processarCortes,
              setOpenModal: setIsOpen,
              loadingAba,
            }}
            transcription={{
              ...transcription,
              ataRef: transcriptionRef,
              transcription: transcriptionList,
              marking: marking,
              modelList: modelList,
              transcriptionSelect: transcriptionSelect,
              setTranscriptionSelect: setTranscriptionSelect,
              selectTemplate: selectTemplate,
              setSelectTemplate: setSelectTemplate,
              setShowModelInput: setShowModelInput,
              showModelInput: showModelInput,
              getAllByHearingFileId: getAllByHearingFileId,
              tryAgain,
              hearingId: hearingId,
              loadingAba,
              hearingFileId: Number(hearingFileId),
            }}
            ata={{
              ataRef: AtaRef,
              buttonFunction: saveAta,
              buttonTitle: t("common.save"),
              disabledButton: false,
              status: 0,
              ata: ata,
              ataText,
              setAtaText,
              modelList,
              setSelectTemplate: setSelectAtaTemplate,
              selectTemplate: selectAtaTemplate,
              loadingAba,
              changedTextAta: changedTextAta,
              setChangedTextAta: setChangedTextAta,
              submited: submited,
              setSubmited: setSubmited,
            }}
            title={t("audiences.titleWithNumber", {
              number: query.get("audienceNumber") || "",
            })}
            videoComponente={{
              videoUrl: `${getRawQueryValue("audienceVideo")}` || "",
              isBegining,
              modalIsOpen: false,
              setMarking,
              marking,
              isExternal: isExternalSourcePlatform(
                Number(query.get("platform")),
              ),
            }}
            subTitle=""
            hearing={audience}
          />
        ) : (
          <SearchEmpty
            typeIcon="avatar"
            textMessage={t("common.noPermissionForProfile")}
            marginTop="200px"
          />
        )}
      </Container>
      <Drawer
        open={isOpen}
        onClose={() => {
          closeModal();
          setSubject("");
          setAttendantId(undefined);
        }}
        title={isEdit ? t("marking.editMarking") : t("marking.createMarking")}
        paddingLeft={28}
        paddingRight={32}
        onApply={isEdit ? handleUpdateMarking : createNewMarking}
      >
        <MarkingChildrenModal
          isEdit={isEdit}
          setAttendantId={setAttendantId}
          attendantId={attendantId}
          subject={subject}
          setSubject={setSubject}
          toEditItem={itemMarking}
          marking={marking}
          error={markingError}
          isBegining={isBegining}
          hasTriedSubmit={hasTriedSubmit}
        />
      </Drawer>

      <ModalDefault
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        title={t("marking.deleteMarking")}
        bodyMessageTitle={t("marking.confirmDelete")}
        bodyMessageSubTitle={t("common.actionCannotBeUndone")}
        textConfirm={t("common.delete").toUpperCase()}
        onClickConfirm={deleteMarking}
        textCancel={t("common.cancel").toUpperCase()}
        bodyMessageMarginBetweenTitleAndSubTitle="30px"
        bodyMessageMarginTop="30px"
        onClickCancel={() => {
          setShowDeleteModal(false);
        }}
      />

      <LoadSpinner loading={loading} />
    </Fragment>
  );
};
